import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMESStore } from '@/stores/useMESStore';
import { useAgentStore, type UIAction } from '@/agent/agentStore';
import { wait } from '@/agent/utils';
import { executeAgentTool } from '@/integrations/api/agent_data_api';
import { getOrCreateSessionId } from '@/agent/sessionId';

const HIGHLIGHT_CLASSES = ['animate-pulse', 'ring-2', 'ring-primary'];

function applyHighlight(selector: string, durationMs = 2000) {
  const el = document.querySelector(selector);
  if (!el) {
    console.warn(`[UIBridge] highlight: no element matches "${selector}"`);
    return;
  }
  el.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => {
    el.classList.remove(...HIGHLIGHT_CLASSES);
  }, durationMs);
}

export function useUIBridge() {
  const navigate = useNavigate();

  const executeActions = useCallback(
    async (actions: UIAction[]) => {
      for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        try {
          switch (action.type) {
            case 'navigate':
              navigate(action.to);
              break;

            case 'toast': {
              const fn = toast[action.level] ?? toast.info;
              fn(action.message);
              break;
            }

            case 'set_active_wo':
              useMESStore.getState().setActiveOrder(action.orderId);
              toast.info(`Orden activa: ${action.orderId}`);
              break;

            case 'highlight':
              applyHighlight(action.selector, action.durationMs ?? 2000);
              break;

            case 'set_filter': {
              const mes = useMESStore.getState();
              if (action.view === 'dispatch_board') {
                mes.setDispatchFilter({ [action.field]: action.value } as Parameters<typeof mes.setDispatchFilter>[0]);
              } else if (action.view === 'quality_board') {
                mes.setQualityFilter({ [action.field]: action.value } as Parameters<typeof mes.setQualityFilter>[0]);
              } else if (action.view === 'wip_board') {
                mes.setWIPFilter({ [action.field]: action.value } as Parameters<typeof mes.setWIPFilter>[0]);
              }
              useAgentStore.getState().updateMemory({
                lastFilter: { view: action.view, field: action.field, value: action.value },
              });
              toast.info(`Filtro aplicado: ${action.field}=${String(action.value)}`);
              break;
            }

            case 'set_search': {
              await wait(200);
              const selectors = [
                `[data-search-view="${action.view}"]`,
                `[data-search]`,
                `input[placeholder*="buscar" i]`,
                `input[placeholder*="search" i]`,
              ];
              let inputEl: HTMLInputElement | null = null;
              for (const sel of selectors) {
                inputEl = document.querySelector(sel) as HTMLInputElement | null;
                if (inputEl) break;
              }
              if (inputEl) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                  window.HTMLInputElement.prototype,
                  'value',
                )?.set;
                nativeInputValueSetter?.call(inputEl, action.value);
                inputEl.dispatchEvent(new Event('input', { bubbles: true }));
                inputEl.focus();
                toast.info(`Búsqueda: ${action.value}`);
              } else {
                console.warn(`[UIBridge] set_search: no input found for view "${action.view}"`);
                toast.info(`Busca "${action.value}" en el cuadro de la vista`);
              }
              break;
            }

            case 'select_item': {
              const selector = `[data-id="${action.id}"]`;
              // Wait one frame so navigation has time to render the target page
              await wait(150);
              const el = document.querySelector(selector) as HTMLElement | null;
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                applyHighlight(selector, 3000);
              } else {
                console.warn('[UIBridge] select_item: element not found', selector);
              }
              break;
            }

            case 'open_detail': {
              const selector = `[data-id="${action.id}"]`;
              await wait(150);
              const el = document.querySelector(selector) as HTMLElement | null;
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                applyHighlight(selector, 3000);
              }
              toast.info(`Detalle · ${action.view}: ${action.id}`);
              break;
            }

            case 'confirm':
              window.dispatchEvent(
                new CustomEvent('agent:confirm:request', {
                  detail: { prompt: action.prompt, onConfirmToken: action.onConfirmToken },
                }),
              );
              break;

            case 'execute_tool': {
              const { tool_name, params, idempotency_key, success_message, error_message } = action;
              try {
                const data = await executeAgentTool({
                  idempotency_key,
                  session_id: getOrCreateSessionId(),
                  tool_name,
                  params,
                });
                if (data?.ok) {
                  toast.success(success_message ?? `${tool_name} ejecutada`);
                } else {
                  const msg = data?.error ?? error_message ?? 'No se pudo ejecutar la acción';
                  toast.error(msg);
                  console.warn('[UIBridge] execute_tool failed', tool_name, data);
                }
              } catch (err) {
                console.error('[UIBridge] execute_tool error', tool_name, err);
                toast.error(error_message ?? `Error ejecutando ${tool_name}`);
              }
              break;
            }
          }
        } catch (err) {
          console.error('[UIBridge] action failed', action, err);
        }

        if (i < actions.length - 1) {
          await wait(250);
        }
      }
    },
    [navigate],
  );

  return { executeActions };
}

export type UIBridge = ReturnType<typeof useUIBridge>;
